import React, { useState, useCallback, useMemo, memo, useEffect, ReactNode } from 'react';

// Type definitions
interface HelmetProps {
  children: React.ReactElement;
}

interface AnalyticsEvent {
  event: string;
  properties: Record<string, any>;
  timestamp: string;
}

interface ConversionOptions {
  outputFormat: OutputFormat;
  conversionMode: ConversionMode;
  pageRange: string;
  includeImages: boolean;
  includeTextBoxes: boolean;
  preserveFormatting: boolean;
  includeMetadata: boolean;
  includeHeaders: boolean;
  includeFooters: boolean;
  includeTables: boolean;
  textExtraction: TextExtractionMode;
  imageHandling: ImageHandling;
  qualityLevel: QualityLevel;
  ocrEnabled: boolean;
  ocrLanguage: string;
  removeWatermarks: boolean;
  rotationAngle: number;
  contrast: number;
  brightness: number;
  cleanupText: boolean;
  pageLayout: PageLayout;
  fontHandling: FontHandling;
  includeAnnotations: boolean;
  includeHyperlinks: boolean;
  includeBookmarks: boolean;
  encoding: string;
  documentTemplate: string;
  passwordProtection: boolean;
  password: string;
  compressionLevel: CompressionLevel;
}

interface ThemeContextType {
  isDark: boolean;
  toggleTheme: () => void;
}

interface AnalyticsHook {
  track: (event: string, properties?: Record<string, any>) => void;
}

interface SEOData {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  twitterCard: string;
}

interface AnalyticsStats {
  total: number;
  conversions: number;
  validations: number;
}

interface PDFInfo {
  fileName: string;
  fileSize: number;
  pageCount: number;
  version: string;
  hasImages: boolean;
  hasTables: boolean;
  hasAnnotations: boolean;
  hasForms: boolean;
  hasBookmarks: boolean;
  hasHeaders: boolean;
  hasFooters: boolean;
  hasHyperlinks: boolean;
  isPasswordProtected: boolean;
  metadata: {
    title?: string;
    author?: string;
    subject?: string;
    creator?: string;
    producer?: string;
    creationDate?: string;
    modificationDate?: string;
  };
}

// Output format options
type OutputFormat = 'docx' | 'doc' | 'rtf' | 'odt' | 'txt';
type ConversionMode = 'accurate-layout' | 'editable-text' | 'text-only' | 'image-to-text' | 'table-focused';
type TextExtractionMode = 'layout-preserved' | 'reading-order' | 'raw-text' | 'structured-text';
type ImageHandling = 'preserve' | 'compress' | 'remove' | 'extract' | 'optimize';
type QualityLevel = 'fast' | 'balanced' | 'high' | 'ultra';
type PageLayout = 'preserve-original' | 'single-column' | 'auto-flow' | 'custom';
type FontHandling = 'preserve-fonts' | 'substitute-similar' | 'use-default' | 'embed-fonts';
type CompressionLevel = 'none' | 'low' | 'medium' | 'high' | 'maximum';

// Mock react-helmet-async for SEO
const Helmet: React.FC<HelmetProps> = ({ children }) => {
  useEffect(() => {
    const extractPropsFromChildren = (element: React.ReactElement): any => {
      if (element.props?.children) {
        const childArray = React.Children.toArray(element.props.children);
        const helmetData: any = { meta: [], link: [] };

        childArray.forEach((child: any) => {
          if (React.isValidElement(child)) {
            const childElement = child as React.ReactElement<any>;

            if (childElement.type === 'title') {
              helmetData.title = childElement.props.children;
            } else if (childElement.type === 'meta') {
              helmetData.meta.push(childElement.props);
            } else if (childElement.type === 'link') {
              helmetData.link.push(childElement.props);
            } else if (childElement.type === 'script') {
              if (childElement.props.type === 'application/ld+json') {
                const script = document.createElement('script');
                script.type = 'application/ld+json';
                script.textContent = childElement.props.children;
                document.head.appendChild(script);
              }
            }
          }
        });
        return helmetData;
      }
      return {};
    };

    const helmetData = extractPropsFromChildren(children);

    if (helmetData.title) {
      document.title = helmetData.title;
    }

    const updateMeta = (name: string, content: string): void => {
      let meta = document.querySelector(`meta[name="${name}"]`) ||
        document.querySelector(`meta[property="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        if (name.startsWith('og:') || name.startsWith('twitter:')) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    if (helmetData.meta && Array.isArray(helmetData.meta)) {
      helmetData.meta.forEach((meta: any) => {
        if (meta.name) updateMeta(meta.name, meta.content);
        if (meta.property) updateMeta(meta.property, meta.content);
      });
    }

    if (helmetData.link && Array.isArray(helmetData.link)) {
      helmetData.link.forEach((link: any) => {
        let linkEl = document.querySelector(`link[rel="${link.rel}"]`) as HTMLLinkElement;
        if (!linkEl) {
          linkEl = document.createElement('link') as HTMLLinkElement;
          linkEl.rel = link.rel;
          document.head.appendChild(linkEl);
        }
        Object.keys(link).forEach((attr) => {
          if (attr !== 'rel') linkEl.setAttribute(attr, link[attr]);
        });
      });
    }
  }, [children]);

  return null;
};

// Analytics hook with in-memory storage
const useAnalytics = (): AnalyticsHook => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsEvent[]>([]);

  const track = useCallback((event: string, properties: Record<string, any> = {}): void => {
    console.log('Analytics Event:', event, properties);

    const analyticsEvent: AnalyticsEvent = {
      event,
      properties,
      timestamp: new Date().toISOString()
    };

    setAnalyticsData(prev => [...prev.slice(-99), analyticsEvent]);

    // Also track with gtag if available
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', event, properties);
    }
  }, []);

  return { track };
};

// Theme context
const ThemeContext = React.createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [isDark, setIsDark] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      return prefersDark;
    }
    return false;
  });

  const toggleTheme = useCallback((): void => {
    setIsDark((prev) => !prev);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

const useTheme = (): ThemeContextType => {
  const context = React.useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Mock PDF analysis function
const analyzePDFFile = async (file: File): Promise<PDFInfo> => {
  // Simulate PDF analysis
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  return {
    fileName: file.name,
    fileSize: file.size,
    pageCount: Math.floor(Math.random() * 50) + 1,
    version: '1.4',
    hasImages: Math.random() > 0.5,
    hasTables: Math.random() > 0.3,
    hasAnnotations: Math.random() > 0.6,
    hasForms: Math.random() > 0.7,
    hasBookmarks: Math.random() > 0.4,
    hasHeaders: Math.random() > 0.5,
    hasFooters: Math.random() > 0.5,
    hasHyperlinks: Math.random() > 0.6,
    isPasswordProtected: Math.random() > 0.8,
    metadata: {
      title: 'Sample PDF Document',
      author: 'Document Author',
      subject: 'PDF Analysis',
      creator: 'PDF Creator',
      producer: 'PDF Producer',
      creationDate: '2024-01-01',
      modificationDate: new Date().toISOString().split('T')[0]
    }
  };
};

// Mock PDF to Word conversion
const convertPDFToWord = async (file: File, options: ConversionOptions): Promise<Blob> => {
  // Simulate conversion process
  await new Promise(resolve => setTimeout(resolve, 3000));
  
  // Create mock Word data based on conversion mode
  let mockWordContent = '';
  
  if (options.conversionMode === 'accurate-layout') {
    mockWordContent = `Sample Document Title

This is a sample document converted from PDF to Word format using accurate layout preservation mode. The original formatting, fonts, and layout structure have been maintained as closely as possible.

Key Features Preserved:
• Original text formatting and styles
• Font families and sizes
• Paragraph spacing and alignment
• Headers and footers
• Table structures and data
• Image positioning and sizing

Table Example:
Name\tAge\tDepartment\tSalary
John Doe\t30\tEngineering\t$75,000
Jane Smith\t28\tMarketing\t$65,000
Mike Johnson\t35\tSales\t$70,000

This conversion maintains the document's professional appearance while making it fully editable in Microsoft Word and compatible applications.`;
  } else if (options.conversionMode === 'editable-text') {
    mockWordContent = `Sample Document Title

This document has been converted from PDF to Word with focus on text editability. While some layout elements may have been adjusted, all text content is fully editable and searchable.

Content includes:
- All original text content
- Preserved paragraph structure
- Editable tables and lists
- Searchable text elements
- Compatible formatting

The conversion prioritizes text accessibility and editing capabilities over exact visual reproduction.`;
  } else if (options.conversionMode === 'text-only') {
    mockWordContent = `Sample Document Title

This is a text-only conversion from the original PDF document. All formatting has been simplified to focus on content accessibility and readability.

Sample text content extracted from the PDF document. This mode provides clean, editable text without complex formatting or layout preservation.

Simple table data:
Name: John Doe, Age: 30, Department: Engineering
Name: Jane Smith, Age: 28, Department: Marketing
Name: Mike Johnson, Age: 35, Department: Sales`;
  }

  const mimeType = options.outputFormat === 'docx' ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' :
                   options.outputFormat === 'doc' ? 'application/msword' :
                   options.outputFormat === 'rtf' ? 'application/rtf' :
                   options.outputFormat === 'odt' ? 'application/vnd.oasis.opendocument.text' :
                   'text/plain';

  return new Blob([mockWordContent], { type: mimeType });
};

// Analytics Dashboard Component
const AnalyticsDashboard: React.FC = memo(() => {
  const [analytics, setAnalytics] = useState<AnalyticsEvent[]>([]);
  const { isDark } = useTheme();

  const stats: AnalyticsStats = useMemo(() => {
    const total = analytics.length;
    const conversions = analytics.filter((a) => a.event === 'pdf_converted').length;
    const validations = analytics.filter((a) => a.event === 'pdf_analyzed').length;

    return { total, conversions, validations };
  }, [analytics]);

  return (
    <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
      <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        Usage Analytics
      </h3>
      <div className="grid grid-cols-4 gap-4">
        <div className={`text-center p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
          <div className={`text-2xl font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
            {Math.floor(Math.random() * 100) + 50}
          </div>
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Total Files
          </div>
        </div>
        <div className={`text-center p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
          <div className={`text-2xl font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
            {Math.floor(Math.random() * 50) + 20}
          </div>
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Conversions
          </div>
        </div>
        <div className={`text-center p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
          <div className={`text-2xl font-bold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
            {Math.floor(Math.random() * 30) + 10}%
          </div>
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Success Rate
          </div>
        </div>
        <div className={`text-center p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
          <div className={`text-2xl font-bold ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
            {Math.floor(Math.random() * 1000) + 500}MB
          </div>
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Data Processed
          </div>
        </div>
      </div>
    </div>
  );
});

AnalyticsDashboard.displayName = 'AnalyticsDashboard';

// PDF Preview Component
const PDFPreview: React.FC<{ pdfInfo: PDFInfo | null; isDark: boolean }> = memo(({ pdfInfo, isDark }) => {
  if (!pdfInfo) return null;

  return (
    <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
      <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        PDF Analysis
      </h3>
      
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>File Name:</span>
            <p className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{pdfInfo.fileName}</p>
          </div>
          <div>
            <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>File Size:</span>
            <p className={`${isDark ? 'text-white' : 'text-gray-900'}`}>
              {(pdfInfo.fileSize / 1024 / 1024).toFixed(2)} MB
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Pages:</span>
            <p className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{pdfInfo.pageCount}</p>
          </div>
          <div>
            <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>PDF Version:</span>
            <p className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{pdfInfo.version}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-2 ${pdfInfo.hasTables ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {pdfInfo.hasTables ? 'Has Tables' : 'No Tables'}
            </span>
          </div>
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-2 ${pdfInfo.hasImages ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {pdfInfo.hasImages ? 'Has Images' : 'No Images'}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-2 ${pdfInfo.hasHeaders ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {pdfInfo.hasHeaders ? 'Headers' : 'No Headers'}
            </span>
          </div>
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-2 ${pdfInfo.hasFooters ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {pdfInfo.hasFooters ? 'Footers' : 'No Footers'}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-2 ${pdfInfo.hasHyperlinks ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {pdfInfo.hasHyperlinks ? 'Links' : 'No Links'}
            </span>
          </div>
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-2 ${pdfInfo.isPasswordProtected ? 'bg-orange-500' : 'bg-green-500'}`}></div>
            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {pdfInfo.isPasswordProtected ? 'Protected' : 'Open'}
            </span>
          </div>
        </div>

        {pdfInfo.metadata && (
          <div className={`mt-4 p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
            <h4 className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Metadata</h4>
            <div className="space-y-1 text-sm">
              {pdfInfo.metadata.title && (
                <div>
                  <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Title: </span>
                  <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{pdfInfo.metadata.title}</span>
                </div>
              )}
              {pdfInfo.metadata.author && (
                <div>
                  <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Author: </span>
                  <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{pdfInfo.metadata.author}</span>
                </div>
              )}
              {pdfInfo.metadata.creationDate && (
                <div>
                  <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Created: </span>
                  <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{pdfInfo.metadata.creationDate}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

PDFPreview.displayName = 'PDFPreview';

// Main Component Content
const PDFToWordContent: React.FC = () => {
  const { isDark, toggleTheme } = useTheme();
  const { track } = useAnalytics();

  // States
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [pdfInfo, setPdfInfo] = useState<PDFInfo | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  const [isConverting, setIsConverting] = useState<boolean>(false);
  const [conversionProgress, setConversionProgress] = useState<number>(0);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [fileName, setFileName] = useState<string>('converted_document');
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [dragActive, setDragActive] = useState<boolean>(false);

  // Advanced options
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('docx');
  const [conversionMode, setConversionMode] = useState<ConversionMode>('accurate-layout');
  const [pageRange, setPageRange] = useState<string>('all');
  const [includeImages, setIncludeImages] = useState<boolean>(true);
  const [includeTextBoxes, setIncludeTextBoxes] = useState<boolean>(true);
  const [preserveFormatting, setPreserveFormatting] = useState<boolean>(true);
  const [includeMetadata, setIncludeMetadata] = useState<boolean>(true);
  const [includeHeaders, setIncludeHeaders] = useState<boolean>(true);
  const [includeFooters, setIncludeFooters] = useState<boolean>(true);
  const [includeTables, setIncludeTables] = useState<boolean>(true);
  const [textExtraction, setTextExtraction] = useState<TextExtractionMode>('layout-preserved');
  const [imageHandling, setImageHandling] = useState<ImageHandling>('preserve');
  const [qualityLevel, setQualityLevel] = useState<QualityLevel>('balanced');
  const [ocrEnabled, setOcrEnabled] = useState<boolean>(false);
  const [ocrLanguage, setOcrLanguage] = useState<string>('eng');
  const [removeWatermarks, setRemoveWatermarks] = useState<boolean>(false);
  const [rotationAngle, setRotationAngle] = useState<number>(0);
  const [contrast, setContrast] = useState<number>(100);
  const [brightness, setBrightness] = useState<number>(100);
  const [cleanupText, setCleanupText] = useState<boolean>(true);
  const [pageLayout, setPageLayout] = useState<PageLayout>('preserve-original');
  const [fontHandling, setFontHandling] = useState<FontHandling>('preserve-fonts');
  const [includeAnnotations, setIncludeAnnotations] = useState<boolean>(true);
  const [includeHyperlinks, setIncludeHyperlinks] = useState<boolean>(true);
  const [includeBookmarks, setIncludeBookmarks] = useState<boolean>(true);
  const [encoding, setEncoding] = useState<string>('UTF-8');
  const [documentTemplate, setDocumentTemplate] = useState<string>('default');
  const [passwordProtection, setPasswordProtection] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');
  const [compressionLevel, setCompressionLevel] = useState<CompressionLevel>('medium');

  // SEO data
  const seoData: SEOData = useMemo(() => ({
    title: "Free PDF to Word Converter Online - Convert PDF to DOCX/DOC Format",
    description: "Convert PDF documents to Word format online for free. Advanced features include layout preservation, OCR, image handling, and formatting retention. Perfect for document editing and collaboration.",
    keywords: "PDF to Word converter, PDF to DOCX, PDF to DOC, online converter, free PDF converter, PDF editor, Word converter, document converter",
    canonical: "https://yoursite.com/pdf-to-word-converter",
    ogTitle: "PDF to Word Converter - Free Online Tool",
    ogDescription: "Convert your PDF files to Word format instantly. Supports layout preservation, OCR, and advanced formatting options for perfect document conversion.",
    ogImage: "https://yoursite.com/og-pdf-word-converter.jpg",
    twitterCard: "summary_large_image"
  }), []);

  // Handle file selection
  const handleFileSelect = useCallback(async (file: File): Promise<void> => {
    if (!file.type.includes('pdf')) {
      setError('Please select a valid PDF file');
      return;
    }

    if (file.size > 100 * 1024 * 1024) { // 100MB limit
      setError('File size must be less than 100MB');
      return;
    }

    setSelectedFile(file);
    setError('');
    setSuccess('');
    setPdfInfo(null);
    
    // Set default filename
    const nameParts = file.name.split('.');
    if (nameParts.length > 1) {
      nameParts.pop();
      setFileName(nameParts.join('.'));
    } else {
      setFileName(file.name);
    }

    track('pdf_file_selected', {
      fileName: file.name,
      fileSize: file.size
    });

    // Analyze PDF
    setIsAnalyzing(true);
    try {
      const info = await analyzePDFFile(file);
      setPdfInfo(info);
      track('pdf_analyzed', {
        pageCount: info.pageCount,
        hasImages: info.hasImages,
        hasTables: info.hasTables,
        hasAnnotations: info.hasAnnotations,
        hasForms: info.hasForms,
        hasBookmarks: info.hasBookmarks,
        hasHeaders: info.hasHeaders,
        hasFooters: info.hasFooters,
        hasHyperlinks: info.hasHyperlinks,
        isPasswordProtected: info.isPasswordProtected
      });
    } catch (err) {
      setError(`Failed to analyze PDF: ${err instanceof Error ? err.message : String(err)}`);
      track('pdf_analysis_error', { error: err instanceof Error ? err.message : String(err) });
    } finally {
      setIsAnalyzing(false);
    }
  }, [track]);

  // Handle file upload
  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  // Handle drag and drop
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragActive(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  // Convert PDF to Word
  const convertToWord = useCallback(async (): Promise<void> => {
    if (!selectedFile) {
      setError('Please select a PDF file first');
      return;
    }

    setError('');
    setSuccess('');
    setIsConverting(true);
    setConversionProgress(0);

    const options: ConversionOptions = {
      outputFormat,
      conversionMode,
      pageRange,
      includeImages,
      includeTextBoxes,
      preserveFormatting,
      includeMetadata,
      includeHeaders,
      includeFooters,
      includeTables,
      textExtraction,
      imageHandling,
      qualityLevel,
      ocrEnabled,
      ocrLanguage,
      removeWatermarks,
      rotationAngle,
      contrast,
      brightness,
      cleanupText,
      pageLayout,
      fontHandling,
      includeAnnotations,
      includeHyperlinks,
      includeBookmarks,
      encoding,
      documentTemplate,
      passwordProtection,
      password,
      compressionLevel
    };

    try {
      const startTime = Date.now();

      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setConversionProgress(prev => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 10;
        });
      }, 200);

      const resultBlob = await convertPDFToWord(selectedFile, options);
      
      clearInterval(progressInterval);
      setConversionProgress(100);

      // Download the result
      const url = URL.createObjectURL(resultBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileName || 'converted_document'}.${outputFormat}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      const processingTime = Date.now() - startTime;
      setSuccess(`PDF successfully converted to ${outputFormat.toUpperCase()}! Download started automatically.`);
      
      track('pdf_converted', {
        format: outputFormat,
        mode: conversionMode,
        processingTime,
        fileSize: selectedFile.size,
        pageCount: pdfInfo?.pageCount,
        options: {
          conversionMode,
          qualityLevel,
          ocrEnabled,
          includeImages,
          preserveFormatting,
          pageLayout
        }
      });

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(`Conversion failed: ${errorMessage}`);
      track('conversion_error', { error: errorMessage });
    } finally {
      setIsConverting(false);
      setConversionProgress(0);
    }
  }, [selectedFile, fileName, outputFormat, conversionMode, pageRange, includeImages, includeTextBoxes, preserveFormatting, includeMetadata, includeHeaders, includeFooters, includeTables, textExtraction, imageHandling, qualityLevel, ocrEnabled, ocrLanguage, removeWatermarks, rotationAngle, contrast, brightness, cleanupText, pageLayout, fontHandling, includeAnnotations, includeHyperlinks, includeBookmarks, encoding, documentTemplate, passwordProtection, password, compressionLevel, pdfInfo?.pageCount, track]);

  // Clear all
  const clearAll = useCallback((): void => {
    setSelectedFile(null);
    setPdfInfo(null);
    setError('');
    setSuccess('');
    setFileName('converted_document');
    setConversionProgress(0);
    track('data_cleared');
  }, [track]);

  // Toggle fullscreen
  const toggleFullscreen = useCallback((): void => {
    setIsFullscreen(!isFullscreen);
    track('fullscreen_toggled', { isFullscreen: !isFullscreen });
  }, [isFullscreen, track]);

  return (
    <div className={`min-h-screen transition-colors duration-200 ${isDark ? 'bg-gray-900' : 'bg-gray-50'} ${isFullscreen ? 'fixed inset-0 z-50 overflow-auto' : ''}`}>

      {/* SEO Meta Tags */}
      <Helmet>
        <>
          <title>{seoData.title}</title>
          <meta name="description" content={seoData.description} />
          <meta name="keywords" content={seoData.keywords} />
          <link rel="canonical" href={seoData.canonical} />

          <meta property="og:title" content={seoData.ogTitle} />
          <meta property="og:description" content={seoData.ogDescription} />
          <meta property="og:image" content={seoData.ogImage} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={seoData.canonical} />

          <meta name="twitter:card" content={seoData.twitterCard} />
          <meta name="twitter:title" content={seoData.ogTitle} />
          <meta name="twitter:description" content={seoData.ogDescription} />
          <meta name="twitter:image" content={seoData.ogImage} />

          <meta name="robots" content="index, follow" />
          <meta name="author" content="PDF to Word Converter Tool" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />

          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebApplication",
              "name": "PDF to Word Converter",
              "description": seoData.description,
              "url": seoData.canonical,
              "applicationCategory": "UtilityApplication",
              "operatingSystem": "Any",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
              }
            })}
          </script>
        </>
      </Helmet>

      <div className={`${isFullscreen ? 'p-4' : 'max-w-7xl mx-auto p-6'}`}>

        {/* Header with Controls */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              PDF to Word Converter
            </h1>
            <p className={`mt-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Convert PDF files to Word documents with layout preservation, OCR support, and advanced formatting options
            </p>
          </div>

          <div className="flex space-x-2">
            {/* Analytics Toggle */}
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-900'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
              title="Toggle Analytics"
              type="button"
            >
              📊
            </button>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-900'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
              title="Toggle Theme"
              type="button"
            >
              {isDark ? '☀️' : '🌙'}
            </button>

            {/* Fullscreen Toggle */}
            <button
              onClick={toggleFullscreen}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-900'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
              title="Toggle Fullscreen"
              type="button"
            >
              {isFullscreen ? '📱' : '🖥️'}
            </button>
          </div>
        </div>

        {/* Analytics Dashboard */}
        {showAnalytics && (
          <div className="mb-6">
            <AnalyticsDashboard />
          </div>
        )}

        {/* Description */}
        <div className={`mb-6 p-4 rounded-lg border ${isDark ? 'bg-blue-900/20 border-blue-800 text-blue-200' : 'bg-blue-50 border-blue-100 text-blue-800'}`}>
          <p>
            Convert PDF documents to editable Word format with intelligent layout preservation, OCR support, and advanced formatting retention.
            Perfect for document editing, collaboration, and content reuse.
          </p>
        </div>

        <div className={`grid ${isFullscreen ? 'grid-cols-1 lg:grid-cols-3' : 'grid-cols-1 md:grid-cols-3'} gap-6`}>

          {/* File Upload Section */}
          <div className="flex flex-col h-full">
            <div className={`p-4 rounded-lg shadow-sm mb-4 flex-grow border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex justify-between items-center mb-3">
                <label className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  PDF File Upload
                </label>
                {selectedFile && (
                  <button
                    className={`text-sm transition-colors ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'}`}
                    onClick={clearAll}
                    type="button"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Drag and Drop Area */}
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive
                    ? isDark ? 'border-blue-400 bg-blue-900/20' : 'border-blue-400 bg-blue-50'
                    : isDark ? 'border-gray-600 hover:border-gray-500' : 'border-gray-300 hover:border-gray-400'
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <div className="space-y-4">
                  <div className={`text-4xl ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    📄
                  </div>
                  <div>
                    <p className={`text-lg font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                      Drop your PDF file here
                    </p>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      or click to browse
                    </p>
                  </div>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="pdf-upload"
                  />
                  <label
                    htmlFor="pdf-upload"
                    className={`inline-block px-4 py-2 rounded-lg font-medium cursor-pointer transition-colors ${
                      isDark
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    Select PDF File
                  </label>
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Maximum file size: 100MB
                  </p>
                </div>
              </div>

              {/* File Info */}
              {selectedFile && (
                <div className={`mt-4 p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <h4 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Selected File</h4>
                  <div className="space-y-1 text-sm">
                    <div>
                      <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Name: </span>
                      <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedFile.name}</span>
                    </div>
                    <div>
                      <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Size: </span>
                      <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>
                    <div>
                      <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Type: </span>
                      <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedFile.type}</span>
                    </div>
                  </div>
                  
                  {isAnalyzing && (
                    <div className="mt-3 flex items-center">
                      <svg className="animate-spin h-4 w-4 mr-2 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Analyzing PDF...</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* PDF Preview */}
            {pdfInfo && <PDFPreview pdfInfo={pdfInfo} isDark={isDark} />}
          </div>

          {/* Configuration Section */}
          <div className={`p-4 rounded-lg shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Conversion Settings
            </h3>

            <div className="space-y-4">
              {/* Basic Settings */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Output File Name
                </label>
                <input
                  type="text"
                  className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                  value={fileName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFileName(e.target.value)}
                  placeholder="Enter file name (without extension)"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Output Format
                </label>
                <select
                  className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                  value={outputFormat}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setOutputFormat(e.target.value as OutputFormat)}
                >
                  <option value="docx">Word (.docx)</option>
                  <option value="doc">Word Legacy (.doc)</option>
                  <option value="rtf">Rich Text (.rtf)</option>
                  <option value="odt">OpenDocument (.odt)</option>
                  <option value="txt">Plain Text (.txt)</option>
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Conversion Mode
                </label>
                <select
                  className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                  value={conversionMode}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setConversionMode(e.target.value as ConversionMode)}
                >
                  <option value="accurate-layout">Accurate Layout</option>
                  <option value="editable-text">Editable Text</option>
                  <option value="text-only">Text Only</option>
                  <option value="image-to-text">Image to Text (OCR)</option>
                  <option value="table-focused">Table Focused</option>
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Page Range
                </label>
                <input
                  type="text"
                  className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                  value={pageRange}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPageRange(e.target.value)}
                  placeholder="e.g., 1-5, 8, 10-12 or 'all'"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Quality Level
                </label>
                <select
                  className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                  value={qualityLevel}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setQualityLevel(e.target.value as QualityLevel)}
                >
                  <option value="fast">Fast (Quick processing)</option>
                  <option value="balanced">Balanced (Recommended)</option>
                  <option value="high">High (Better accuracy)</option>
                  <option value="ultra">Ultra (Maximum quality)</option>
                </select>
              </div>

              {/* Advanced Options Toggle */}
              <div className="mb-4">
                <button
                  className={`text-sm flex items-center transition-colors ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  type="button"
                >
                  <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {/* Advanced Settings */}
              {showAdvanced && (
                <div className={`space-y-4 p-3 rounded-lg mb-4 ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  
                  {/* Content Options */}
                  <div className="space-y-3">
                    <h4 className={`font-medium text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Content Options</h4>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="preserveFormatting"
                        checked={preserveFormatting}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPreserveFormatting(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="preserveFormatting" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Preserve original formatting
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="includeImages"
                        checked={includeImages}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIncludeImages(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="includeImages" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Include images and graphics
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="includeTables"
                        checked={includeTables}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIncludeTables(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="includeTables" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Include tables and data
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="includeHeaders"
                        checked={includeHeaders}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIncludeHeaders(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="includeHeaders" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Include headers and footers
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="includeHyperlinks"
                        checked={includeHyperlinks}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIncludeHyperlinks(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="includeHyperlinks" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Preserve hyperlinks
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="includeAnnotations"
                        checked={includeAnnotations}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIncludeAnnotations(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="includeAnnotations" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Include annotations and comments
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="cleanupText"
                        checked={cleanupText}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCleanupText(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="cleanupText" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Clean up extracted text
                      </label>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Page Layout
                      </label>
                      <select
                        className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                        value={pageLayout}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setPageLayout(e.target.value as PageLayout)}
                      >
                        <option value="preserve-original">Preserve Original Layout</option>
                        <option value="single-column">Single Column</option>
                        <option value="auto-flow">Auto Flow</option>
                        <option value="custom">Custom Layout</option>
                      </select>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Font Handling
                      </label>
                      <select
                        className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                        value={fontHandling}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFontHandling(e.target.value as FontHandling)}
                      >
                        <option value="preserve-fonts">Preserve Original Fonts</option>
                        <option value="substitute-similar">Substitute Similar Fonts</option>
                        <option value="use-default">Use Default Fonts</option>
                        <option value="embed-fonts">Embed Fonts</option>
                      </select>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Image Handling
                      </label>
                      <select
                        className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                        value={imageHandling}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setImageHandling(e.target.value as ImageHandling)}
                      >
                        <option value="preserve">Preserve Original Quality</option>
                        <option value="compress">Compress Images</option>
                        <option value="optimize">Optimize for File Size</option>
                        <option value="extract">Extract as Separate Files</option>
                        <option value="remove">Remove Images</option>
                      </select>
                    </div>
                  </div>

                  {/* OCR Options */}
                  <div className="space-y-3">
                    <h4 className={`font-medium text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>OCR & Image Processing</h4>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="ocrEnabled"
                        checked={ocrEnabled}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOcrEnabled(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="ocrEnabled" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Enable OCR for scanned documents
                      </label>
                    </div>

                    {ocrEnabled && (
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          OCR Language
                        </label>
                        <select
                          className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                          value={ocrLanguage}
                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setOcrLanguage(e.target.value)}
                        >
                          <option value="eng">English</option>
                          <option value="spa">Spanish</option>
                          <option value="fra">French</option>
                          <option value="deu">German</option>
                          <option value="ita">Italian</option>
                          <option value="por">Portuguese</option>
                          <option value="rus">Russian</option>
                          <option value="chi_sim">Chinese (Simplified)</option>
                          <option value="chi_tra">Chinese (Traditional)</option>
                          <option value="jpn">Japanese</option>
                          <option value="kor">Korean</option>
                          <option value="ara">Arabic</option>
                          <option value="hin">Hindi</option>
                        </select>
                      </div>
                    )}

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="removeWatermarks"
                        checked={removeWatermarks}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRemoveWatermarks(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="removeWatermarks" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Attempt to remove watermarks
                      </label>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Brightness: {brightness}%
                      </label>
                      <input
                        type="range"
                        min="50"
                        max="150"
                        value={brightness}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBrightness(parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Contrast: {contrast}%
                      </label>
                      <input
                        type="range"
                        min="50"
                        max="150"
                        value={contrast}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setContrast(parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                    </div>
                  </div>

                  {/* Security Options */}
                  <div className="space-y-3">
                    <h4 className={`font-medium text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Security & Output</h4>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="passwordProtection"
                        checked={passwordProtection}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPasswordProtection(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="passwordProtection" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Password protect output file
                      </label>
                    </div>

                    {passwordProtection && (
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          Password
                        </label>
                        <input
                          type="password"
                          className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                          value={password}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                          placeholder="Enter password"
                        />
                      </div>
                    )}

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Compression Level
                      </label>
                      <select
                        className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                        value={compressionLevel}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setCompressionLevel(e.target.value as CompressionLevel)}
                      >
                        <option value="none">No compression</option>
                        <option value="low">Low compression</option>
                        <option value="medium">Medium compression</option>
                        <option value="high">High compression</option>
                        <option value="maximum">Maximum compression</option>
                      </select>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Character Encoding
                      </label>
                      <select
                        className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                        value={encoding}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setEncoding(e.target.value)}
                      >
                        <option value="UTF-8">UTF-8 (Recommended)</option>
                        <option value="UTF-16">UTF-16</option>
                        <option value="ASCII">ASCII</option>
                        <option value="ISO-8859-1">ISO-8859-1</option>
                        <option value="Windows-1252">Windows-1252</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Convert Button */}
              <div className="mt-6">
                <button
                  className={`w-full px-4 py-3 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    selectedFile && !isConverting
                      ? isDark
                        ? 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500'
                        : 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500'
                      : isDark
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                  onClick={convertToWord}
                  disabled={!selectedFile || isConverting}
                  type="button"
                >
                  {isConverting ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Converting... {Math.round(conversionProgress)}%
                    </div>
                  ) : (
                    `Convert to ${outputFormat.toUpperCase()}`
                  )}
                </button>

                {isConverting && (
                  <div className="mt-2">
                    <div className={`w-full bg-gray-200 rounded-full h-2 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${conversionProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className={`p-4 rounded-lg shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Conversion Results
            </h3>

            {/* Status Messages */}
            {error && (
              <div className={`border p-3 rounded-lg mb-4 ${isDark ? 'bg-red-900/20 border-red-800 text-red-200' : 'bg-red-50 border-red-200 text-red-700'}`}>
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}

            {success && (
              <div className={`border p-3 rounded-lg mb-4 ${isDark ? 'bg-green-900/20 border-green-800 text-green-200' : 'bg-green-50 border-green-200 text-green-700'}`}>
                <div className="flex items-center mb-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="font-medium">Success!</p>
                </div>
                <p>{success}</p>
              </div>
            )}

            {!selectedFile && !error && !success && (
              <div className={`border p-8 rounded-lg flex items-center justify-center ${isDark ? 'bg-gray-700 border-gray-600 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p>
                    Upload a PDF file to start conversion
                  </p>
                </div>
              </div>
            )}

            {selectedFile && !error && !success && !isConverting && (
              <div className={`border p-6 rounded-lg ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                <div className="text-center">
                  <div className={`text-4xl mb-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    📝
                  </div>
                  <p className={`text-lg font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    Ready to Convert
                  </p>
                  <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Click the convert button to transform your PDF into an editable {outputFormat.toUpperCase()} document
                  </p>
                </div>
              </div>
            )}

            {/* Conversion Tips */}
            <div className={`mt-6 p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <h4 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>💡 Conversion Tips</h4>
              <ul className={`text-sm space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li>• Use "Accurate Layout" mode for precise visual reproduction</li>
                <li>• Choose "Editable Text" for maximum text editing flexibility</li>
                <li>• Enable OCR for scanned documents or image-based PDFs</li>
                <li>• Preserve formatting to maintain original document appearance</li>
                <li>• Test with a small page range first for large documents</li>
              </ul>
            </div>

            {/* Quick Actions */}
            {selectedFile && (
              <div className="mt-4 flex space-x-2">
                <button
                  className={`flex-1 px-3 py-2 rounded-lg font-medium transition-colors ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
                  onClick={clearAll}
                  type="button"
                >
                  🗑️ Clear All
                </button>
                <button
                  className={`flex-1 px-3 py-2 rounded-lg font-medium transition-colors ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
                  onClick={() => {
                    setOutputFormat('docx');
                    setConversionMode('accurate-layout');
                    setQualityLevel('balanced');
                    track('settings_reset');
                  }}
                  type="button"
                >
                  🔄 Reset Settings
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Information Section */}
        <div className={`mt-8 p-6 rounded-lg shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>About PDF to Word Conversion</h2>

          <div className="space-y-4">
            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>How This Converter Works</h3>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                This advanced PDF to Word converter uses sophisticated algorithms to analyze PDF document structure, extract content with 
                formatting preservation, and convert it into fully editable Word documents. The tool supports multiple output formats 
                including DOCX, DOC, RTF, ODT, and TXT, ensuring compatibility with Microsoft Word, Google Docs, LibreOffice, and other 
                word processing applications.
              </p>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Conversion Modes</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>Accurate Layout</strong> - Preserves original PDF layout, fonts, and formatting as closely as possible</li>
                <li><strong>Editable Text</strong> - Prioritizes text editability while maintaining reasonable formatting</li>
                <li><strong>Text Only</strong> - Extracts plain text content without complex formatting or layout</li>
                <li><strong>Image to Text (OCR)</strong> - Uses OCR to extract text from scanned documents and images</li>
                <li><strong>Table Focused</strong> - Optimized for documents with primarily tabular data</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Output Format Options</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>Word (.docx)</strong> - Modern Microsoft Word format with full feature support</li>
                <li><strong>Word Legacy (.doc)</strong> - Compatible with older Word versions (97-2003)</li>
                <li><strong>Rich Text (.rtf)</strong> - Universal format supported by most word processors</li>
                <li><strong>OpenDocument (.odt)</strong> - Open standard format for LibreOffice and other tools</li>
                <li><strong>Plain Text (.txt)</strong> - Simple text format without formatting</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Advanced Features</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>Layout Preservation</strong> - Maintains original document layout, columns, and positioning</li>
                <li><strong>Font Handling</strong> - Preserves or substitutes fonts for consistent appearance</li>
                <li><strong>Image Processing</strong> - Preserves, compresses, or optimizes images based on preferences</li>
                <li><strong>Table Recognition</strong> - Intelligent detection and conversion of tabular data</li>
                <li><strong>Header/Footer Support</strong> - Preserves document headers and footers</li>
                <li><strong>Hyperlink Preservation</strong> - Maintains clickable links and references</li>
                <li><strong>OCR Technology</strong> - Extracts text from scanned documents in 13+ languages</li>
                <li><strong>Annotation Support</strong> - Includes comments and markup from original PDF</li>
                <li><strong>Password Protection</strong> - Secure output documents with password encryption</li>
                <li><strong>Batch Processing</strong> - Convert specific page ranges or entire documents</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Use Cases</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>Document Editing</strong> - Convert PDFs to editable Word format for revisions and updates</li>
                <li><strong>Content Reuse</strong> - Extract content from PDFs for use in new documents and presentations</li>
                <li><strong>Collaboration</strong> - Share editable versions of PDF documents with team members</li>
                <li><strong>Template Creation</strong> - Convert PDF forms and templates to editable Word templates</li>
                <li><strong>Academic Work</strong> - Edit research papers, theses, and academic documents</li>
                <li><strong>Business Documents</strong> - Process contracts, reports, and proposals for editing</li>
                <li><strong>Legal Documents</strong> - Convert legal documents while preserving formatting and structure</li>
                <li><strong>Translation</strong> - Extract text for translation while maintaining document structure</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Quality Levels</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>Fast</strong> - Quick processing with basic conversion (30-60 seconds)</li>
                <li><strong>Balanced</strong> - Good quality with reasonable processing time (1-3 minutes)</li>
                <li><strong>High</strong> - Enhanced accuracy with advanced layout preservation (3-5 minutes)</li>
                <li><strong>Ultra</strong> - Maximum quality with all enhancement features (5-10 minutes)</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Best Practices</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li>Use "Accurate Layout" mode for documents where visual appearance is critical</li>
                <li>Choose "Editable Text" mode when maximum text editing flexibility is needed</li>
                <li>Enable OCR for scanned documents or when text appears as images</li>
                <li>Preserve formatting for professional documents and reports</li>
                <li>Include images and graphics for complete document conversion</li>
                <li>Test conversion settings with a small page range for large documents</li>
                <li>Use appropriate quality levels based on document complexity and time constraints</li>
                <li>Consider font handling options based on target audience and platform compatibility</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Technical Specifications</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>Input:</strong> PDF files up to 100MB in size</li>
                <li><strong>Output:</strong> DOCX, DOC, RTF, ODT, and TXT formats</li>
                <li><strong>Processing:</strong> Client-side conversion for privacy and security</li>
                <li><strong>OCR:</strong> 13 language support with advanced text recognition</li>
                <li><strong>Encoding:</strong> UTF-8, UTF-16, ASCII, and other character encodings</li>
                <li><strong>Compression:</strong> Configurable compression levels for output files</li>
                <li><strong>Security:</strong> Optional password protection for converted documents</li>
                <li><strong>Compatibility:</strong> Works with all modern browsers and operating systems</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrapper component with ThemeProvider
const PDFToWord: React.FC = () => {
  return (
    <ThemeProvider>
      <PDFToWordContent />
    </ThemeProvider>
  );
};

export default PDFToWord;